import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';

// Import updated reducers (now using DynamicCase/DynamicField)
import appSlice from './slices/appSlice';
import fieldsSlice from './slices/fieldsSlice';        // Now uses DynamicField
import casesSlice from './slices/casesSlice';          // Now uses DynamicCase
import sessionsSlice from './slices/sessionsSlice';    // Simplified (no categoriesCovered)
import messagesSlice from './slices/messagesSlice';
import websocketSlice from './slices/websocketSlice';
import speechSlice from './slices/speechSlice';
import avatarSlice from './slices/avatarSlice';
import diagnosisSlice from './slices/diagnosisSlice';  // Simplified (no category tracking)
import processingSlice from './slices/processingSlice'; // NEW: LLM processing state
import themeSlice from './slices/themeSlice';          // NEW: Theme management

// Import API slice (updated for new types)
import { apiSlice } from './api/apiSlice';

// Import middleware
import { websocketMiddleware } from './middleware/websocketMiddleware';
import { speechMiddleware } from './middleware/speechMiddleware';
import { avatarMiddleware } from './middleware/avatarMiddleware';

// Import updated types
import type { RootState } from './types';
import authSlice from './slices/authSlice';

// Configure store with updated reducers
export const store = configureStore({
  reducer: {
    // App state
    app: appSlice,
    
    // Data (simple arrays, now using DynamicCase/DynamicField)
    fields: fieldsSlice,     // Updated for DynamicField
    cases: casesSlice,       // Updated for DynamicCase
    
    // Session management (simplified)
    sessions: sessionsSlice, // Removed categoriesCovered tracking
    
    // Real-time features
    messages: messagesSlice,
    websocket: websocketSlice,
    speech: speechSlice,
    avatar: avatarSlice,
    diagnosis: diagnosisSlice,   // Simplified (no category tracking)

    // Auth state
    auth: authSlice,
    
    // LLM Processing state (NEW)
    processing: processingSlice, // NEW: Tracks LLM case processing
    
    // Theme state (NEW)
    theme: themeSlice,       // NEW: Theme management
    
    // RTK Query API (updated for new endpoints)
    api: apiSlice.reducer,
  },
  
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Updated serializable check configuration
      serializableCheck: {
        ignoredActions: [
          // Ignore WebSocket actions with non-serializable payloads
          'websocket/messageReceived',
          'speech/transcriptReceived',
          'speech/toggle', // Add this to ignore
          'avatar/streamReady',
          // Ignore RTK Query actions
          'api/queries/pending',
          'api/queries/fulfilled',
          'api/queries/rejected',
          'api/mutations/pending',
          'api/mutations/fulfilled',
          'api/mutations/rejected',
          // Ignore thunk actions
          'speech/toggleSpeech/pending',
          'speech/toggleSpeech/fulfilled',
          'speech/toggleSpeech/rejected',
          // Ignore processing actions with timestamps
          'processing/startProcessing',
          'processing/updateProcessingStatus',
          'processing/completeProcessing',
        ],
        ignoredActionPaths: [
          'meta.arg',
          'meta.baseQueryMeta',
          'payload.timestamp',
          'error.stack',
          // Processing-related paths
          'payload.extractedData',
          'payload.scoringRubric',
        ],
        ignoredPaths: [
          // Ignore non-serializable state paths
          'websocket.socket',
          'speech.mediaRecorder',
          'avatar.videoStream',
          // Ignore RTK Query cache
          'api.queries',
          'api.mutations',
          'api.provided',
          'api.subscriptions',
          // Processing paths with complex objects
          'processing.activeProcessing.*.extractedData',
          'processing.activeProcessing.*.scoringRubric',
          'processing.backgroundProcessingStatus.extractedData',
          'processing.backgroundProcessingStatus.scoringRubric',
        ],
      },
      
      // Configure immutability check
      immutableCheck: {
        ignoredPaths: [
          'websocket.socket',
          'speech.mediaRecorder',
          'avatar.videoStream',
          'api.queries',
          'api.mutations',
          'api.provided',
          'api.subscriptions',
          // Processing paths
          'processing.activeProcessing',
        ],
      },
    })
    .concat([
      // Custom middleware - ORDER MATTERS!
      // WebSocket first (for basic connectivity)
      websocketMiddleware,
      // Speech second (depends on WebSocket)
      speechMiddleware, 
      // Avatar third (depends on WebSocket for responses)
      avatarMiddleware,
      // RTK Query middleware last
      apiSlice.middleware,
    ]),
  
  // Enable Redux DevTools in development
  devTools: process.env.NODE_ENV !== 'production',
});

// Set up RTK Query listeners
setupListeners(store.dispatch);

// Export types
export type AppDispatch = typeof store.dispatch;
export type { RootState };

// Export typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;